import { useCallback, useEffect, useLayoutEffect, useRef, type FocusEvent } from "react";
import {
  useReducedMotion,
  type AnimationPlaybackControls,
  type AnimationScope,
  type AnimationSequence,
  type DOMKeyframesDefinition,
  type SegmentTransitionOptions,
  type useAnimate,
} from "framer-motion";

/**
 * A scene's entrance is a TIMELINE: an ordered list of steps that framer-motion
 * plays as one sequence. Each step waits for the one before it to finish
 * (pull it earlier with `at: "-0.2"`), so elements arrive one after another
 * and how long each waits falls out of the kind of transition before it.
 *
 * Keyframes are always explicit [from, ..., to] arrays. The first frame of
 * every property doubles as the element's pre-entrance POSE, which the engine
 * applies before first paint and again whenever the scene is left.
 */
export type Keyframes = Record<string, ReadonlyArray<string | number>>;

export interface DomStep {
  kind: "dom";
  /** CSS selector, resolved inside the scene. Matches play in document order. */
  target: string;
  keyframes: Keyframes;
  options: SegmentTransitionOptions;
}

/** A step driven by a progress callback (0 → 1), for effects CSS can't tween, e.g. text scramble. */
export interface FnStep {
  kind: "fn";
  target: string;
  render: (els: HTMLElement[], progress: number) => void;
  /** A function of the matches, so e.g. a typewriter's duration can follow its length. */
  options: (els: HTMLElement[]) => SegmentTransitionOptions;
}

export type Step = DomStep | FnStep;

/** Builders may return several steps; nesting is flattened. */
export type Timeline = ReadonlyArray<Step | ReadonlyArray<Step>>;

type ScopedAnimate = ReturnType<typeof useAnimate>[1];

const flatten = (timeline: Timeline): Step[] => timeline.flat() as Step[];

const resolve = (scope: HTMLElement, target: string) =>
  Array.from(scope.querySelectorAll<HTMLElement>(target));

/**
 * Put every target at one end of its timeline, instantly: `frame` 0 is the
 * pre-entrance pose (earliest step wins per property), -1 the settled state.
 */
function snapTo(scope: HTMLElement, timeline: Timeline, animate: ScopedAnimate, frame: 0 | -1) {
  const steps = flatten(timeline);
  if (frame === -1) steps.reverse();
  const claimed = new Map<Element, Set<string>>();
  for (const step of steps) {
    const els = resolve(scope, step.target);
    if (els.length === 0) continue;
    if (step.kind === "fn") {
      step.render(els, frame === 0 ? 0 : 1);
      continue;
    }
    for (const el of els) {
      let props = claimed.get(el);
      if (!props) claimed.set(el, (props = new Set()));
      const values: Record<string, string | number> = {};
      for (const [prop, frames] of Object.entries(step.keyframes)) {
        if (props.has(prop)) continue;
        props.add(prop);
        values[prop] = frames.at(frame) as string | number;
      }
      if (Object.keys(values).length > 0) {
        animate(el, values as DOMKeyframesDefinition, { duration: 0 });
      }
    }
  }
}

/**
 * Rendered at the current breakpoint (a `hidden sm:block` target on a phone is
 * not). SVG children are judged by their <svg>: shapes inside a <mask> or
 * <defs> have no boxes of their own yet still draw.
 */
const rendered = (el: Element) =>
  ((el instanceof SVGElement && el.ownerSVGElement) || el).getClientRects().length > 0;

/** Put a step's targets straight into their settled state. */
function settleStep(step: Step, els: HTMLElement[], animate: ScopedAnimate) {
  if (step.kind === "fn") {
    step.render(els, 1);
    return;
  }
  const values: Record<string, string | number> = {};
  for (const [prop, frames] of Object.entries(step.keyframes)) values[prop] = frames.at(-1) as string | number;
  for (const el of els) animate(el, values as DOMKeyframesDefinition, { duration: 0 });
}

function toSequence(scope: HTMLElement, timeline: Timeline, animate: ScopedAnimate): AnimationSequence {
  const sequence: AnimationSequence = [];
  for (const step of flatten(timeline)) {
    const all = resolve(scope, step.target);
    const els = all.filter(rendered);
    // Targets hidden at this breakpoint skip the show but land in their final
    // state, so a resize that reveals them mid-visit finds them in place.
    if (els.length < all.length) settleStep(step, all.filter((el) => !rendered(el)), animate);
    // Skip steps with nothing visible to animate, so neither their duration nor
    // their `at` offset leaves a dead pause in the timeline.
    if (els.length === 0) continue;
    if (step.kind === "fn") {
      sequence.push([(progress: number) => step.render(els, progress), step.options(els)]);
    } else {
      sequence.push([els, step.keyframes as unknown as DOMKeyframesDefinition, step.options]);
    }
  }
  return sequence;
}

/**
 * Plays `timeline` every time the scene arrives and re-poses it once the page
 * has settled elsewhere (by then the scene is off-screen, so the reset is never
 * seen). Under reduced motion nothing is posed or played: the scene renders in
 * its final state.
 *
 * Returns a focus handler for the scene: keyboard focus landing on something
 * inside it fast-forwards the entrance, so a focused control is never one
 * that hasn't faded in yet.
 */
export function useChoreography(
  scope: AnimationScope<HTMLElement>,
  animate: ScopedAnimate,
  timeline: Timeline,
  arrived: boolean,
) {
  const reduce = useReducedMotion();
  const controls = useRef<AnimationPlaybackControls | null>(null);
  /** The entrance has been shown since the scene was last posed, so leaving must re-pose it. */
  const shown = useRef(false);
  /** Focus already brought the scene in; its coming arrival must not replay the entrance. */
  const skipNext = useRef(false);
  const arrivedRef = useRef(arrived);
  useLayoutEffect(() => {
    arrivedRef.current = arrived;
  });

  // Before first paint: wait offstage.
  useLayoutEffect(() => {
    const el = scope.current;
    if (!el) return;
    controls.current?.stop();
    controls.current = null;
    if (reduce) snapTo(el, timeline, animate, -1);
    else if (!arrivedRef.current) {
      snapTo(el, timeline, animate, 0);
      shown.current = false;
    }
  }, [scope, animate, timeline, reduce]);

  useEffect(() => {
    const el = scope.current;
    if (!el || reduce) return;
    if (arrived) {
      shown.current = true;
      if (skipNext.current) {
        skipNext.current = false;
        return;
      }
      controls.current?.stop();
      const playing = animate(toSequence(el, timeline, animate));
      controls.current = playing;
      playing.then(() => {
        if (controls.current === playing) controls.current = null;
      });
    } else if (shown.current) {
      // Off-screen now: re-arm for the next arrival.
      controls.current?.stop();
      controls.current = null;
      shown.current = false;
      skipNext.current = false;
      snapTo(el, timeline, animate, 0);
    }
  }, [arrived, scope, animate, timeline, reduce]);

  return useCallback(
    (e: FocusEvent<HTMLElement>) => {
      const el = scope.current;
      // The scene itself takes focus from in-page links; let that arrival play.
      if (!el || reduce || e.target === e.currentTarget) return;
      if (controls.current) {
        controls.current.complete();
        controls.current = null;
        return;
      }
      if (shown.current) return;
      snapTo(el, timeline, animate, -1);
      shown.current = true;
      if (!arrivedRef.current) skipNext.current = true;
    },
    [scope, animate, timeline, reduce],
  );
}
